import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, LoginRequest, LoginResponse } from '@shared/types'
import { apiClient, getApiErrorMessage } from '../lib/api-client'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: User | null
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => void
  updateUser: (user: User) => void
  isLoading: boolean
  isLoggingIn: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      verifyToken()
    } else {
      setIsLoading(false)
    }
  }, [])

  const verifyToken = async () => {
    try {
      const response = await apiClient.get<{ user: User }>('/auth/me')
      setUser(response.data.user)
    } catch (error) {
      console.error('Token verification failed:', error)
      localStorage.removeItem('token')
      delete apiClient.defaults.headers.common['Authorization']
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (credentials: LoginRequest) => {
    setIsLoggingIn(true)
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', credentials)
      const { token, user: nextUser } = response.data

      localStorage.setItem('token', token)
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      setUser(nextUser)
      toast.success('Welcome back')
    } catch (error: unknown) {
      const message = getApiErrorMessage(error, 'Invalid email or password. Please try again.')
      const enrichedError = Object.assign(new Error(message), { cause: error })
      throw enrichedError
    } finally {
      setIsLoggingIn(false)
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    delete apiClient.defaults.headers.common['Authorization']
    setUser(null)
    toast.success('Logged out successfully')
  }

  const updateUser = (nextUser: User) => {
    setUser(nextUser)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        updateUser,
        isLoading,
        isLoggingIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
